/**
   Copyright (c)2021 3Key Company s.r.o., All Rights Reserved

   It is strictly prohibited to distribute the source code,
   transpiled and compiled code or its parts without previous
   agreement of the 3Key Company board.
*/

import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import { DateTime } from "lib/DateTime";

import { AffectedConfigurations, CVEData } from "data/dto/company/CVEData";
import { CveListDTO } from "data/dto/company/CveListDTO";

export interface CVEsState {

   companyId?: string;

   dateRange: number;
   dateStartEnabled: boolean;
   dateStart: string;
   dateEndEnabled: boolean;
   dateEnd: string;

   cvesLoading: boolean;
   cveList: CveListDTO;

   cveAffectedConfigsLoading: boolean;

   selectedCve?: CVEData;
   selectedCveAffectedConfigs?: AffectedConfigurations[];

}


const initialState: CVEsState = {

   dateRange: 4, // for ranges refer to CVElist.tsx
   dateStartEnabled: true,
   dateStart: DateTime.toYYYYMMDD(new Date().setHours(0, 0, 0) - 24 * 60 * 60 * 1000 * 7),
   dateEndEnabled: true,
   dateEnd: DateTime.toYYYYMMDD(new Date().setHours(0, 0, 0)),

   cvesLoading: false,
   cveList: { from: 0, totalCount: 0, data: [] },

   cveAffectedConfigsLoading: false

}


export const slice = createSlice({

   name: "cves",

   initialState,

   reducers: {

      setCompany: (state, action: PayloadAction<{ companyId?: string }>) => {
         state.companyId = action.payload.companyId;
      },

      setDateRange: (state, action: PayloadAction<{
         range: number,
         dateStartEnabled?: boolean,
         dateEndEnabled?: boolean,
         dateStart?: string,
         dateEnd?: string
      }>) => {
         state.dateRange = action.payload.range;
         if (action.payload.dateEndEnabled !== undefined) state.dateStartEnabled = action.payload.dateEndEnabled;
         if (action.payload.dateEndEnabled !== undefined) state.dateEndEnabled = action.payload.dateEndEnabled;
         if (action.payload.dateStart !== undefined) state.dateStart = action.payload.dateStart;
         if (action.payload.dateEnd !== undefined) state.dateEnd = action.payload.dateEnd;
      },

      setDateStartEnabled: (state, action: PayloadAction<{ enabled: boolean }>) => {
         state.dateStartEnabled = action.payload.enabled;
      },

      setDateStart: (state, action: PayloadAction<{ date: string }>) => {
         state.dateStart = action.payload.date;
      },

      setDateEndEnabled: (state, action: PayloadAction<{ enabled: boolean }>) => {
         state.dateEndEnabled = action.payload.enabled;
      },

      setDateEnd: (state, action: PayloadAction<{ date: string }>) => {
         state.dateEnd = action.payload.date;
      },

      selectCve: (state, action: PayloadAction<{ cveId?: string }>) => {
         if (action.payload.cveId) {
            state.selectedCve = state.cveList.data.find(c => c.id === action.payload.cveId);
            state.selectedCveAffectedConfigs = undefined;
         } else {
            state.selectedCve = undefined;
            state.selectedCveAffectedConfigs = undefined;
         }
      },

      clearCves: (state) => {
         state.cveList = { from: 0, totalCount: 0, data: [] };
         state.selectedCve = undefined;
         state.selectedCveAffectedConfigs = undefined;
      },

      loadCves: (state, action: PayloadAction<{ from?: number }>) => {
         state.cvesLoading = true;
      },

      cvesLoaded: (state, action: PayloadAction<{ cveList: CveListDTO }>) => {
         state.cvesLoading = false;
         state.cveList = action.payload.cveList;
         if (state.selectedCve && !state.cveList.data.find(c => c.id === state.selectedCve?.id)) {
            state.selectedCve = undefined;
            state.selectedCveAffectedConfigs = undefined;
         }
      },

      cvesLoadingError: (state, action: PayloadAction<{ err: any }>) => {
         state.cvesLoading = false;
         console.error(action.payload.err);
      },

      loadCveAffectedConfigs: (state) => {
         state.cveAffectedConfigsLoading = true;
      },

      cveAffectedConfigsLoaded: (state, action: PayloadAction<{ data: AffectedConfigurations[]}>) => {
         state.cveAffectedConfigsLoading = false;
         state.selectedCveAffectedConfigs = action.payload.data;
      },

      cveAffectedConfigsLoadingError: (state, action: PayloadAction<{ err: any }>) => {
         state.cveAffectedConfigsLoading = false;
         console.error(action.payload.err)
      }


   }

})

export const actions = slice.actions;

export default slice.reducer;

